import { Box, Button, Paper, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CARD_SHADOW, COLORS, PLAN_INFO_BAR, PLAN_SUCCESS } from './tokens';
import type { PlanSignOff } from './planRecordApi';
import { displayYear, formatPlanDate } from './planRecordView';

export function PlanBar({ icon, title, body, action }: { icon?: React.ReactNode; title: string; body: string; action?: React.ReactNode }) {
  return (
    <Paper elevation={0} sx={{ bgcolor: '#FFFFFF', borderRadius: '12px', boxShadow: CARD_SHADOW, p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
        {icon}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 600, lineHeight: '28px', color: COLORS.ink }}>{title}</Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 500, lineHeight: '24px', color: COLORS.muted }}>{body}</Typography>
        </Box>
      </Box>
      {action}
    </Paper>
  );
}

export function DraftBanner({ firstName, districtName, year }: { firstName: string; districtName: string; year: string }) {
  return (
    <PlanBar
      icon={<InfoOutlinedIcon sx={{ color: PLAN_INFO_BAR.icon, fontSize: 24, flexShrink: 0 }} />}
      title="This plan is still a draft"
      body={`${districtName} is preparing ${firstName}'s ${displayYear(year)} plan from the spring ACCESS results. You'll be asked to review it once it's finalized. You can add notes now.`}
    />
  );
}

export function SignOffBar({
  firstName,
  districtName,
  year,
  finalizedAt,
  finalizedBy,
  currentVersion,
  revisedWhat,
  reviewed,
  reviewers,
  mySignOff,
  onSignOff,
  onAddNote,
}: {
  firstName: string;
  districtName: string;
  year: string;
  finalizedAt: string;
  finalizedBy: string;
  currentVersion: number;
  revisedWhat: string;
  reviewed: number;
  reviewers: number;
  mySignOff: PlanSignOff | null;
  onSignOff: () => void;
  onAddNote: () => void;
}) {
  const count = reviewers > 1 ? `${reviewed} of ${reviewers} teachers have reviewed it` : '';
  const primary = (label: string) => (
    <Button variant="contained" onClick={onSignOff} sx={{ textTransform: 'none', fontWeight: 600, fontSize: 16, bgcolor: COLORS.navy, flexShrink: 0, '&:hover': { bgcolor: '#23236F' } }}>
      {label}
    </Button>
  );
  if (!mySignOff) {
    return (
      <PlanBar
        title={`Have you reviewed ${firstName}'s ${displayYear(year)} plan?`}
        body={`Your sign-off tells ${districtName} you've seen this plan and its accommodations. Finalized ${formatPlanDate(finalizedAt)} by ${finalizedBy}${count ? ` · ${count}` : ''}.`}
        action={primary("I've reviewed this plan")}
      />
    );
  }
  if (mySignOff.version < currentVersion) {
    return (
      <PlanBar
        title="This plan changed since you reviewed it"
        body={`Revised ${formatPlanDate(finalizedAt)} by ${finalizedBy}: ${revisedWhat || 'plan content changed'}. Your ${formatPlanDate(mySignOff.signedAt)} sign-off stays on the earlier version; review this one when you've read the changes.`}
        action={primary("I've reviewed this version")}
      />
    );
  }
  return (
    <PlanBar
      icon={<CheckCircleOutlineIcon sx={{ color: PLAN_SUCCESS, fontSize: 24, flexShrink: 0 }} />}
      title={`You reviewed this plan on ${formatPlanDate(mySignOff.signedAt)}`}
      body={`${count ? `${count}. ` : ''}If the plan is revised, you'll be asked to review the new version.`}
      action={
        <Button onClick={onAddNote} sx={{ textTransform: 'none', fontWeight: 600, fontSize: 16, color: COLORS.navy, flexShrink: 0 }}>
          Add a note
        </Button>
      }
    />
  );
}
